import React, { SyntheticEvent, useEffect, useMemo } from "react";

import {
  Button,
  Input,
  InputProps,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "semantic-ui-react";

import moment from "moment";

import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";

import SnackMessage from "react-lib/apps/common/SnackMessage";

import CardSummaryTrainingKPIUX from "./CardSummaryTrainingKPIUX";

import {
  ACTIONS,
  BTN_ACTS,
  PickedState,
  RunSequence,
  SEQUENCE_NAME,
  SetProp,
  State,
  TrainingSummaryType,
} from "./sequence/SummaryTrainingKPI";

import "./TableEmployee.scss";

import { MONTH_OPTION } from "react-lib/utils/dateUtils";

type CardSummaryTrainingKPIProps = Pick<PickedState, "buttonLoadCheck" | "errorMessage"> & {
  setProp: SetProp;
  runSequence: RunSequence;
  SummaryTrainingKPISequence: State["SummaryTrainingKPISequence"];
  divisionOptions: Record<string, any>[];
  onViewTrainingSchedule: (data: TrainingSummaryType) => void;
};

const CURRENT_YEAR = Number(moment().format("YYYY"));

const CardSummaryTrainingKPI = (props: CardSummaryTrainingKPIProps) => {
  useEffect(() => {
    props.runSequence({ sequence: "SummaryTrainingKPI", restart: true });
  }, []);

  const yearOptions = useMemo(() => {
    const years = Array.from({ length: 111 })
      .fill("")
      .map((unused, index) => CURRENT_YEAR - 100 + index);

    return years.map((year) => ({ key: year, text: year + 543, value: year }));
  }, []);

  const summaryItems = useMemo(
    () => props.SummaryTrainingKPISequence?.items ?? [],
    [props.SummaryTrainingKPISequence?.items]
  );

  const enabledSave = useMemo(() => {
    if (summaryItems.length === 0) {
      return false;
    }

    const isAllScoresEmpty = summaryItems.every((item) => !item.score);
    const isSomeItemsEditing = summaryItems.some((item) => item.is_editing);

    return isAllScoresEmpty || isSomeItemsEditing;
  }, [summaryItems]);

  const latestEdited = useMemo(() => {
    const datetime = summaryItems[0]?.latest_edit_datetime;
    const user = summaryItems[0]?.latest_edit_user;

    return user ? `${user} ${datetime}` : "";
  }, [summaryItems]);

  const handleSearch = () => {
    props.runSequence({
      sequence: "SummaryTrainingKPI",
      action: ACTIONS.SEARCH,
    });
  };

  const handleChangeFilter = (e: SyntheticEvent | null, data: Record<string, any>) => {
    const name = data.name;

    props.setProp("SummaryTrainingKPISequence.filter", {
      ...props.SummaryTrainingKPISequence?.filter,
      [name]: data.value,
    });
  };

  const handleChangeAchieved = (index: number) => (e: SyntheticEvent, data: InputProps) => {
    const items: any[] = [...summaryItems];

    items[index].score = data.value;
    items[index].is_editing = true;

    props.setProp(`SummaryTrainingKPISequence.items`, [...items]);
  };

  const handleSave = () => {
    props.runSequence({ sequence: "SummaryTrainingKPI", action: ACTIONS.SAVE });
  };

  const handlePrintReport = () => {
    props.runSequence({ sequence: "SummaryTrainingKPI", action: ACTIONS.PRINT_REPORT });
  };

  return (
    <div style={{ marginTop: "-1.5rem" }}>
      <SnackMessage
        error={props.errorMessage?.[SEQUENCE_NAME]}
        success={null}
        onClose={() => {
          props.setProp(`errorMessage.${SEQUENCE_NAME}`, null);
        }}
      />

      <CardSummaryTrainingKPIUX
        filter={props.SummaryTrainingKPISequence?.filter}
        latestEdited={latestEdited}
        divisionOptions={props.divisionOptions}
        monthOptions={MONTH_OPTION}
        yearOptions={yearOptions}
        onChangeFilter={handleChangeFilter}
        buttonPrint={
          <ButtonLoadCheck
            setProp={props.setProp}
            color="yellow"
            icon="print"
            paramKey={BTN_ACTS.PRINT_REPORT}
            size="small"
            title="Print Report"
            buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.PRINT_REPORT]}
            onClick={handlePrintReport}
          />
        }
        buttonSave={
          <ButtonLoadCheck
            setProp={props.setProp}
            color="green"
            disabled={!enabledSave}
            paramKey={BTN_ACTS.SAVE}
            size="small"
            title="SAVE"
            buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.SAVE]}
            onClick={handleSave}
          />
        }
        buttonSearch={
          <ButtonLoadCheck
            setProp={props.setProp}
            color="blue"
            paramKey={BTN_ACTS.SEARCH}
            size="small"
            title="SEARCH"
            buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.SEARCH]}
            onClick={handleSearch}
          />
        }
        table={
          <div className="table-st-kpi">
            <Table celled compact structured>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell rowSpan={2} textAlign="center">
                    Employee code
                  </TableHeaderCell>
                  <TableHeaderCell rowSpan={2} textAlign="center">
                    Employee name
                  </TableHeaderCell>
                  <TableHeaderCell rowSpan={2} textAlign="center">
                    Position
                  </TableHeaderCell>
                  <TableHeaderCell colSpan={3} textAlign="center">
                    Training Course/ month
                  </TableHeaderCell>
                  <TableHeaderCell colSpan={3} textAlign="center">
                    Training Hours/ month
                  </TableHeaderCell>
                  <TableHeaderCell colSpan={3} textAlign="center">
                    Training Score/ month
                  </TableHeaderCell>
                  <TableHeaderCell style={{ width: "55px" }} rowSpan={2} />
                </TableRow>
                <TableRow>
                  <TableHeaderCell textAlign="center">Required</TableHeaderCell>
                  <TableHeaderCell textAlign="center">Participated</TableHeaderCell>
                  <TableHeaderCell textAlign="center">Result</TableHeaderCell>
                  <TableHeaderCell textAlign="center">Required</TableHeaderCell>
                  <TableHeaderCell textAlign="center">Participated</TableHeaderCell>
                  <TableHeaderCell textAlign="center">Result</TableHeaderCell>
                  <TableHeaderCell textAlign="center">Required</TableHeaderCell>
                  <TableHeaderCell textAlign="center">Achieved</TableHeaderCell>
                  <TableHeaderCell textAlign="center">Result</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(props.SummaryTrainingKPISequence?.items ?? []).length === 0 ? (
                  <TableCell style={{ color: "grey" }} colSpan="16" textAlign="center">
                    No rows found
                  </TableCell>
                ) : null}

                {(props.SummaryTrainingKPISequence?.items ?? []).map((item, index) => (
                  <TableRow key={`row-${index}`}>
                    <TableCell textAlign="center">{item.employee_code}</TableCell>
                    <TableCell textAlign="center">{item.employee_name}</TableCell>
                    <TableCell textAlign="center">{item.employee_position}</TableCell>
                    <TableCell textAlign="center">{item.minimum_course_per_month}</TableCell>
                    <TableCell textAlign="center">{item.summary_course_per_month}</TableCell>
                    <TableCell textAlign="center">
                      <label
                        style={{
                          color: item.result_course_per_month === "PASS" ? "#1B9D2C" : "#C60000",
                        }}
                      >
                        {item.result_course_per_month}
                      </label>
                    </TableCell>
                    <TableCell textAlign="center">{item.minimum_hour_per_month}</TableCell>
                    <TableCell textAlign="center">{item.summary_hour_per_month}</TableCell>
                    <TableCell textAlign="center">
                      <label
                        style={{
                          color: item.result_hour_per_month === "PASS" ? "#1B9D2C" : "#C60000",
                        }}
                      >
                        {item.result_hour_per_month}
                      </label>
                    </TableCell>
                    <TableCell textAlign="center">{item.minimum_score_per_month}</TableCell>
                    <TableCell textAlign="center">
                      <Input
                        style={{ minWidth: "4.25rem" }}
                        min={0}
                        size="small"
                        type="number"
                        value={item.score}
                        fluid
                        onChange={handleChangeAchieved(index)}
                        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                          if (/^[e\-+.]$/.test(e.key)) {
                            e.preventDefault();
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell textAlign="center">
                      <label
                        style={{
                          color:
                            Number(item.score) >= Number(item.minimum_score_per_month || 0)
                              ? "#1B9D2C"
                              : "#C60000",
                        }}
                      >
                        {Number(item.score) >= Number(item.minimum_score_per_month || 0)
                          ? "PASS"
                          : "FAIL"}
                      </label>
                    </TableCell>
                    <TableCell textAlign="center">
                      <Button
                        style={{ fontSize: "0.8rem", padding: "0.4rem" }}
                        color="grey"
                        icon="eye"
                        onClick={() => {
                          props.onViewTrainingSchedule(item);
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        }
      />
    </div>
  );
};

CardSummaryTrainingKPI.displayName = "CardSummaryTrainingKPI";

export default React.memo(CardSummaryTrainingKPI);
